import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Plus, RefreshCw, Pencil, Trash2, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';

const MarketTab = () => {
  const [prices, setPrices] = useState([]);
  const [total, setTotal] = useState(0);
  const [brandFilter, setBrandFilter] = useState('');
  const [brands, setBrands] = useState([]);
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [newItem, setNewItem] = useState({ brand: '', model_key: '', model_display: '', new_price: '' });
  const [scraping, setScraping] = useState(false);

  const load = useCallback(() => {
    const params = { page, limit: 50 };
    if (brandFilter) params.brand = brandFilter;
    adminAPI.getMarketPrices(params).then(r => { setPrices(r.data.prices); setTotal(r.data.total); }).catch(() => {});
  }, [page, brandFilter]);
  useEffect(load, [load]);
  useEffect(() => { adminAPI.getKnownModels().then(r => setBrands(Object.keys(r.data))).catch(() => {}); }, []);

  const handleSave = async () => {
    if (editItem) {
      await adminAPI.updateManualPrice(editItem.brand, editItem.model_key, { new_price: parseFloat(editItem.new_price), model_display: editItem.model_display });
      setEditItem(null);
    } else {
      await adminAPI.createManualPrice({ ...newItem, new_price: parseFloat(newItem.new_price) });
      setNewItem({ brand: '', model_key: '', model_display: '', new_price: '' });
      setShowAdd(false);
    }
    load();
  };
  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer ${item.model_display} ?`)) return;
    await adminAPI.deleteMarketPrice(item.brand, item.model_key);
    load();
  };
  const triggerScrape = async () => {
    setScraping(true);
    try { await adminAPI.triggerScrape(); } catch {}
    setTimeout(() => setScraping(false), 3000);
  };

  return (
    <div data-testid="admin-market">
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={brandFilter} onChange={e => { setBrandFilter(e.target.value); setPage(1); }}
          className="h-9 px-3 rounded-lg border border-[#D6E8F0] text-sm bg-white">
          <option value="">Toutes les marques</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="bg-[#32CD32] h-9"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>
        <Button size="sm" onClick={triggerScrape} disabled={scraping} variant="outline" className="h-9">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${scraping ? 'animate-spin' : ''}`} /> Scraper
        </Button>
        <span className="ml-auto text-xs text-[#5A8BA8] self-center">{total} prix</span>
      </div>
      {showAdd && (
        <div className="bg-[#F0F7FA] rounded-xl p-4 mb-4 space-y-2" data-testid="add-price-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <input value={newItem.brand} onChange={e => setNewItem(p => ({ ...p, brand: e.target.value }))} placeholder="Marque" className="h-9 px-3 rounded-lg border text-sm" />
            <input value={newItem.model_key} onChange={e => setNewItem(p => ({ ...p, model_key: e.target.value }))} placeholder="Clé modèle" className="h-9 px-3 rounded-lg border text-sm" />
            <input value={newItem.model_display} onChange={e => setNewItem(p => ({ ...p, model_display: e.target.value }))} placeholder="Nom affiché" className="h-9 px-3 rounded-lg border text-sm" />
            <input value={newItem.new_price} onChange={e => setNewItem(p => ({ ...p, new_price: e.target.value }))} placeholder="Prix neuf" type="number" className="h-9 px-3 rounded-lg border text-sm" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} className="bg-[#0C63BF]"><Check className="w-3.5 h-3.5 mr-1" /> Enregistrer</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}><X className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}
      <div className="space-y-1">
        {prices.map(p => (
          <div key={`${p.brand}-${p.model_key}`} className="bg-white rounded-lg border border-[#E8F4FD] p-2.5 sm:p-3 flex items-center gap-2 text-sm">
            {editItem?.model_key === p.model_key && editItem?.brand === p.brand ? (
              <>
                <input value={editItem.model_display} onChange={e => setEditItem(prev => ({ ...prev, model_display: e.target.value }))} className="flex-1 h-8 px-2 rounded border text-sm" />
                <input value={editItem.new_price} onChange={e => setEditItem(prev => ({ ...prev, new_price: e.target.value }))} type="number" className="w-24 h-8 px-2 rounded border text-sm" />
                <button onClick={handleSave} className="p-1.5 rounded bg-green-100 text-green-700"><Check className="w-3.5 h-3.5" /></button>
                <button onClick={() => setEditItem(null)} className="p-1.5 rounded bg-gray-100 text-gray-500"><X className="w-3.5 h-3.5" /></button>
              </>
            ) : (
              <>
                <span className="text-[10px] text-[#5A8BA8] uppercase font-medium w-20 shrink-0">{p.brand}</span>
                <span className="flex-1 text-[#033871] font-medium truncate">{p.model_display}</span>
                <span className="font-bold text-[#0C63BF] shrink-0">{p.new_price?.toFixed(0)}€</span>
                {p.manual_override && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Manuel</span>}
                <button onClick={() => setEditItem({ ...p })} className="p-1.5 rounded hover:bg-[#F0F7FA] text-[#5A8BA8]"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => handleDelete(p)} className="p-1.5 rounded hover:bg-red-50 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        ))}
      </div>
      {total > 50 && (
        <div className="flex justify-center items-center gap-3 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg border disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-[#5A8BA8]">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={prices.length < 50} className="p-1.5 rounded-lg border disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

export default MarketTab;
